import { useState } from 'react';
import './postseason-view.styles.scss';
import { Link } from 'react-router-dom';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import BracketView from '@/components/SHARED/bracket-view/bracket-view.component';
import BracketDiagram from '@/components/SHARED/bracket-diagram/bracket-diagram.component';
import { emptyNationalBracket, nationalRegionCount } from '@shared/bracketShape';
import HelpLink from '@/components/SHARED/help-link/help-link.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import ManagerIcon from '@/components/SHARED/manager-icon/manager-icon.component';
import PostseasonRace from '@/components/SHARED/postseason-race/postseason-race.component';

// The league's postseason: conference tournaments, Selection Day and the
// national tournament (college) or the playoffs (pro).
// postseason: summary/postseason (null before it starts).
// part (college): 'conference' for the conference tournaments page (with
// the race for them during the regular season), 'national' for the
// national tournament page; left out, everything (past seasons).
const PostseasonView = ({ league, postseason, teams, teamLink, gameLink, highlightId, part }) => {

    const showConf = part !== 'national';
    const showNational = part !== 'conference';
    const myConf = highlightId ? teams?.[highlightId]?.conference : null;
    const national = showNational ? postseason?.brackets?.national : null;
    const playoffs = postseason?.brackets?.playoffs;
    const confBrackets = !showConf ? [] : Object.values(postseason?.brackets ?? {})
        .filter(b => b.style === 'conference')
        .sort((a, b) => a.name.localeCompare(b.name));
    const selection = showNational ? postseason?.selection : null;
    const sections = [
        national && { id: 'national', label: 'National tournament' },
        selection && { id: 'selection', label: 'Selection' },
        confBrackets.length > 0 && { id: 'conference', label: 'Conference tournaments' },
    ].filter(Boolean);
    const [ section, setSection ] = useState(null);
    const [ conf, setConf ] = useState(null);
    const ps = league.settings.postseason;
    const inSeason = ['preseason', 'regular', 'regular-complete'].includes(league.season.phase);

    if (part === 'national' && !selection) {
        return <NationalPreview league={league} postseason={postseason} teams={teams} teamLink={teamLink} highlightId={highlightId} />;
    }

    if (!postseason) {
        return (
            <div className='postseason-view'>
                <div className='g-card'>
                    <p className='meta'>
                        The postseason starts after the regular season ends on day {league.season.totalDays}.
                        {league.type === 'pro' ? ' Here is where the playoff race stands.' :
                            ps.conferenceTournaments ? ' Conference tournaments come first; the national tournament has its own page.' :
                                ' The national tournament has its own page.'}
                        <HelpLink section='postseason' label='How the postseason works' />
                    </p>
                </div>
                {inSeason && <PostseasonRace league={league} teams={teams} teamLink={teamLink} highlightId={highlightId} />}
            </div>
        );
    }

    if (part === 'conference' && !confBrackets.length) {
        return (
            <div className='g-card postseason-view'>
                <p className='meta'>
                    {ps.conferenceTournaments ? 'There are no conference tournaments to show.' :
                        'This league has no conference tournaments: each regular-season champion gets the automatic bid.'}
                    {' '}The national tournament has its own page.
                </p>
            </div>
        );
    }

    const champion = showNational && postseason.champion && teams?.[postseason.champion];
    const activeSection = section && sections.some(s => s.id === section) ? section : sections[0]?.id;
    const confId = conf ?? (myConf && postseason.brackets[`conf-${myConf}`] ? myConf : confBrackets[0]?.conference);
    const confBracket = postseason.brackets[`conf-${confId}`];
    const shared = { teams, teamLink, gameLink, highlightId };

    return (
        <div className='postseason-view'>
            {champion &&
                <Link to={teamLink(champion.id)} className='g-card champion-card'>
                    <TeamBadge team={champion} size='lg' />
                    <div>
                        <div className='meta'>{league.season.year ? `Season ${league.season.year} champion` : 'Champion'}</div>
                        <div className='champion-name'>{champion.name} {champion.mascot}<ManagerIcon team={champion} /></div>
                    </div>
                </Link>
            }

            {playoffs &&
                <div className='g-card'>
                    <h3>Playoffs</h3>
                    <PlayoffBracket bracket={playoffs} league={league} shared={shared} />
                </div>
            }

            {sections.length > 1 &&
                <div className='section-chips' role='tablist'>
                    {sections.map(s => (
                        <button key={s.id} role='tab' aria-selected={s.id === activeSection}
                            className={`chip ${s.id === activeSection ? 'active' : ''}`}
                            onClick={() => setSection(s.id)}>
                            {s.label}
                        </button>
                    ))}
                </div>
            }

            {activeSection === 'national' && national && <div className='g-card'><NationalBracket bracket={national} shared={shared} /></div>}
            {activeSection === 'selection' && <SelectionList selection={selection} champions={postseason.champions} league={league} {...shared} />}
            {activeSection === 'conference' && confBracket &&
                <div className='g-card'>
                    <div className='conf-picker field'>
                        <label htmlFor='conf-bracket'>Conference</label>
                        <select id='conf-bracket' value={confId} onChange={(e) => setConf(e.target.value)}>
                            {confBrackets.map(b => <option key={b.conference} value={b.conference}>{b.name}</option>)}
                        </select>
                    </div>
                    {confBracket.champion &&
                        <p className='bracket-champ'>Champion: <b>{teams?.[confBracket.champion]?.name}</b> (automatic bid)</p>
                    }
                    <BracketView key={confBracket.id} bracket={confBracket} {...shared} />
                </div>
            }
        </div>
    )
}

// The national tournament page before Selection Day: how the field is
// picked, and the automatic bids won so far.
const NationalPreview = ({ league, postseason, teams, teamLink, highlightId }) => {
    const ps = league.settings.postseason;
    const champions = Object.entries(postseason?.champions ?? {})
        .map(([confId, teamId]) => ({ conf: league.conferences[confId], teamId }))
        .filter(c => c.conf)
        .sort((a, b) => a.conf.name.localeCompare(b.conf.name));
    const regionCount = nationalRegionCount(ps.fieldSize);
    return (
        <div className='postseason-view'>
            <div className='g-card'>
                <h3>National tournament</h3>
                <p className='meta'>
                    {ps.fieldSize} teams make the national tournament.
                    {ps.conferenceTournaments ?
                        ' Each conference tournament champion gets an automatic bid.' :
                        ' Each conference\'s regular-season champion gets an automatic bid.'}
                    {' '}On Selection Day, after the {ps.conferenceTournaments ? 'conference tournaments' : 'regular season'}, the rest are picked by team rating and quad record.
                    The Rankings page shows where teams stand.
                    <HelpLink section='postseason' label='How the postseason works' />
                </p>
            </div>
            <div className='g-card'>
                <h3>The bracket</h3>
                <p className='meta'>
                    {league.season.phase === 'selection' ?
                        'Selection Day: the field and seeds are set when the next game day is played.' :
                        'Filled in on Selection Day.'}
                    {regionCount > 1 && ` Four regions of ${ps.fieldSize / 4}, seeded 1-${ps.fieldSize / 4}; the region winners meet in the Final Four.`}
                </p>
                <NationalBracket bracket={emptyNationalBracket(ps.fieldSize)} shared={{ teams, teamLink, gameLink: () => '', highlightId }} />
            </div>
            {champions.length > 0 &&
                <div className='g-card selection-list'>
                    <h3>Automatic bids so far</h3>
                    {champions.map(c => (
                        <div key={c.conf.id} className='auto-bid'>
                            <span className='meta'>{c.conf.name}</span>
                            <Link to={teamLink(c.teamId)} className={`sel-team ${c.teamId === highlightId ? 'mine' : ''}`}>
                                <TeamBadge team={teams?.[c.teamId]} size='sm' /> <span className='sel-name'><TeamName team={teams?.[c.teamId]} id={c.teamId} /></span>
                            </Link>
                        </div>
                    ))}
                </div>
            }
        </div>
    )
}

// The pro playoffs. When each conference has its own side of the bracket,
// the conference rounds can be shown one conference at a time.
const PlayoffBracket = ({ bracket, league, shared }) => {
    const confIds = [...new Set(bracket.rounds[0].matchups
        .flatMap(m => [m.top?.conference, m.bottom?.conference]).filter(Boolean))];
    const [ tab, setTab ] = useState(() => {
        const mine = shared.highlightId && shared.teams?.[shared.highlightId]?.conference;
        return confIds.includes(mine) ? mine : 'all';
    });
    if (confIds.length < 2) return <BracketView key='playoffs' bracket={bracket} {...shared} />;
    // Each conference fills an equal block of every round up to its final.
    const inConf = (m) => {
        const per = bracket.rounds[m.round].matchups.length / confIds.length;
        return confIds[Math.floor(m.index / per)] === tab;
    };
    const confRounds = bracket.rounds.map((_, i) => i)
        .filter(i => bracket.rounds[i].matchups.length >= confIds.length);
    const tabs = [{ id: 'all', label: 'All conferences' }, ...confIds.map(id => ({ id, label: league.conferences[id]?.name ?? id }))];
    return (
        <div className='bracket-section'>
            <div className='field region-picker'>
                <label htmlFor='playoff-conference'>Conference</label>
                <select id='playoff-conference' value={tab} onChange={(e) => setTab(e.target.value)}>
                    {tabs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
            </div>
            {tab === 'all' ?
                <BracketView key='all' bracket={bracket} {...shared} /> :
                <BracketView key={tab} bracket={bracket} rounds={confRounds} filter={inConf} {...shared} />
            }
        </div>
    )
}

// The national tournament by region, then the Final Four: drawn as a
// bracket, or listed a round at a time with every game's details. bracket
// may be emptyNationalBracket() (before Selection Day).
const NationalBracket = ({ bracket, shared }) => {
    const regionCount = bracket.regionOrder?.length ?? 1;
    const regionRounds = regionCount > 1 ? bracket.rounds.length - 2 : bracket.rounds.length;
    const regionName = (pos) => bracket.regionNames[bracket.regionOrder[pos]];
    const tabs = regionCount > 1 ? [
        ...bracket.regionOrder.map((_, pos) => ({ id: pos, label: `${regionName(pos)} Region` })),
        { id: 'final', label: 'Final Four' },
    ] : [];
    // Open on the Final Four once the regions are done, else on the
    // manager's region.
    const regionsDone = !bracket.empty && bracket.rounds[regionRounds - 1]?.matchups.every(m => m.winner);
    const [ tab, setTab ] = useState(() => {
        if (regionsDone) return 'final';
        const perRegion = bracket.rounds[0].matchups.length / regionCount;
        const mine = bracket.rounds[0].matchups.find(m => shared.highlightId &&
            (m.top?.team === shared.highlightId || m.bottom?.team === shared.highlightId));
        return mine ? Math.floor(mine.index / perRegion) : 0;
    });
    const [ view, setView ] = useState('bracket');
    const allRounds = bracket.rounds.map((_, i) => i);
    const listView = view === 'games' && !bracket.empty;

    const viewChips = !bracket.empty &&
        <div className='section-chips view-chips' role='tablist'>
            {[{ id: 'bracket', label: 'Bracket' }, { id: 'games', label: 'Games' }].map(v => (
                <button key={v.id} role='tab' aria-selected={v.id === view}
                    className={`chip ${v.id === view ? 'active' : ''}`}
                    onClick={() => setView(v.id)}>
                    {v.label}
                </button>
            ))}
        </div>;

    if (regionCount === 1) {
        return (
            <div className='bracket-section'>
                {viewChips}
                {listView ?
                    <BracketView key='national' bracket={bracket} {...shared} /> :
                    <BracketDiagram bracket={bracket} winnerLabel='Champion' {...shared} />
                }
            </div>
        );
    }
    const inRegion = (m) => {
        const perRegion = bracket.rounds[m.round].matchups.length / regionCount;
        return Math.floor(m.index / perRegion) === tab;
    };
    // Final Four slots before they're filled: which region's winner.
    const semiLabel = (m, side) => m.round === regionRounds ?
        `${regionName(2 * m.index + (side === 'bottom' ? 1 : 0))} winner` : null;
    return (
        <div className='bracket-section'>
            <div className='nb-bar'>
                <div className='field region-picker'>
                    <label htmlFor='national-region'>Region</label>
                    <select id='national-region' value={tab}
                        onChange={(e) => setTab(e.target.value === 'final' ? 'final' : Number(e.target.value))}>
                        {tabs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                </div>
                {viewChips}
            </div>
            {tab === 'final' ?
                (listView ?
                    <BracketView key='final' bracket={bracket} rounds={allRounds.slice(regionRounds)} {...shared} /> :
                    <BracketDiagram key='final' bracket={bracket} rounds={allRounds.slice(regionRounds)}
                        emptyLabel={semiLabel} winnerLabel='Champion' {...shared} />) :
                (listView ?
                    <BracketView key={`region-${tab}`} bracket={bracket} rounds={allRounds.slice(0, regionRounds)} filter={inRegion} {...shared} /> :
                    <BracketDiagram key={`region-${tab}`} bracket={bracket} rounds={allRounds.slice(0, regionRounds)}
                        filter={inRegion} winnerLabel={`${regionName(tab)} champion`} {...shared} />)
            }
        </div>
    )
}

// Selection Day results: the field by seed line, and the bubble.
const SelectionList = ({ selection, champions, league, teams, teamLink, highlightId }) => {
    const autoFor = Object.fromEntries(Object.entries(champions ?? {}).map(([c, t]) => [t, c]));
    const teamRow = (id, extra) => {
        const t = teams?.[id];
        return (
            <Link to={teamLink(id)} className={`sel-team ${id === highlightId ? 'mine' : ''}`}>
                <TeamBadge team={t} size='sm' /> <span className='sel-name'><TeamName team={t} id={id} /></span>
                {extra}
            </Link>
        );
    };
    return (
        <div className='selection-list'>
            <div className='g-card'>
                <h3>The field</h3>
                <p className='meta'>
                    Conference champions get automatic bids; the rest are picked by team rating and quad record.
                    Teams are seeded 1-{selection.field.length} and spread across the regions so the best teams meet as late as possible.
                </p>
                <div className='regions'>
                    {selection.regions.map((region, r) => (
                        <section key={r} className='region'>
                            <h4>{region.name}{selection.regions.length > 1 ? ' Region' : ''}</h4>
                            <ol className='seed-list'>
                                {[...region.teams].sort((a, b) => a.seed - b.seed).map(e => (
                                    <li key={e.team}>
                                        <span className='seed'>{e.seed}</span>
                                        {teamRow(e.team, e.auto && <span className='auto-tag' title={league.conferences[autoFor[e.team]]?.name}>auto</span>)}
                                    </li>
                                ))}
                            </ol>
                        </section>
                    ))}
                </div>
            </div>
            <div className='bubble'>
                <div className='g-card'>
                    <h3>Last four in</h3>
                    {selection.lastFourIn.length ? selection.lastFourIn.map(id => <div key={id}>{teamRow(id)}</div>) : <p className='meta'>None</p>}
                </div>
                <div className='g-card'>
                    <h3>First four out</h3>
                    {selection.firstFourOut.length ? selection.firstFourOut.map(id => <div key={id}>{teamRow(id)}</div>) : <p className='meta'>None</p>}
                </div>
            </div>
        </div>
    )
}

export default PostseasonView
